import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Archive,
  CalendarDays,
  Eye,
  EyeOff,
  ExternalLink,
  Film,
  FolderInput,
  FolderOpen,
  FolderPlus,
  Images,
  MousePointerClick,
  Music,
  ScrollText,
  Tag,
  Tags,
  Trash2,
  UserPlus,
  Users,
} from 'lucide-react';
import { AppLayout } from '@/components/Layout/AppLayout';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useConfirm } from '@/components/common/ConfirmDialog';
import { useAuthStore } from '@/store/useAuthStore';
import { useUploadUrl } from '@/hooks/useUploadUrl';
import { QUERY_KEYS } from '@/api/queryKeys';
import {
  fetchArchive,
  fetchArchiveStats,
  uploadArchiveFiles,
  updateArchiveItem,
  deleteArchiveItem,
  batchUpdateArchive,
  saveMarkierungen,
  MEDIA_CATEGORIES,
  MEDIA_CATEGORY_LABELS,
  MEDIA_CATEGORY_ICONS,
  type ArchiveMedia,
  type MediaCategory,
  type UpdateArchiveInput,
  type ArchiveStats,
  type Markierung,
} from '@/api/archive.api';
import { FotoMarkierungen, type MarkierungsAnzeige } from '@/components/Archive/FotoMarkierungen';
import {
  fetchAlbums,
  createAlbum,
  updateAlbum,
  deleteAlbum,
  type Album,
} from '@/api/albums.api';
import { listBiographies } from '@/api/career.api';
import { PersonAutocomplete } from '@/components/PersonForm/PersonAutocomplete';
import { PersonMultiSelect, type PersonRef } from '@/components/PersonForm/PersonMultiSelect';
import { BiographyView, CAREER_QUERY_KEYS } from '@/components/Archive/BiographyView';

// ─── Hilfsfunktionen ─────────────────────────────────────────────────────────

function KategorieIcon({ category, className }: { category: string; className?: string }) {
  const Icon = MEDIA_CATEGORY_ICONS[category as MediaCategory] ?? MEDIA_CATEGORY_ICONS.other;
  return <Icon className={className} aria-hidden="true" />;
}

function personDisplayName(p: {
  firstName: string;
  lastName: string | null;
  title?: string | null;
}): string {
  return [p.title, p.firstName, p.lastName].filter(Boolean).join(' ');
}

function formatFileSize(bytes: number | null): string {
  if (!bytes) return '–';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('de-DE');
  } catch {
    return iso;
  }
}

function isImage(mime: string | null): boolean {
  return !!mime && mime.startsWith('image/');
}

function isVideo(mime: string | null): boolean {
  return !!mime && mime.startsWith('video/');
}

function isAudio(mime: string | null): boolean {
  return !!mime && mime.startsWith('audio/');
}

function isPdf(mime: string | null): boolean {
  return mime === 'application/pdf';
}

// ─── Upload-Modal ────────────────────────────────────────────────────────────

interface UploadModalProps {
  onClose: () => void;
  albums: Album[];
}

function UploadModal({ onClose, albums }: UploadModalProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [persons, setPersons] = useState<PersonRef[]>([]);
  const [albumId, setAlbumId] = useState<string>('');
  const [category, setCategory] = useState<MediaCategory | ''>('');
  const [tagsInput, setTagsInput] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [sourceRef, setSourceRef] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [location, setLocation] = useState('');
  const [dateTaken, setDateTaken] = useState('');

  const uploadMutation = useMutation({
    mutationFn: () =>
      uploadArchiveFiles({
        files,
        personIds: persons.map((p) => p.id),
        albumId: albumId || undefined,
        category: (category || undefined) as MediaCategory | undefined,
        tags: tagsInput
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        title: title || undefined,
        description: description || undefined,
        sourceRef: sourceRef || undefined,
        sourceUrl: sourceUrl || undefined,
        location: location || undefined,
        dateTaken: dateTaken || undefined,
      }),
    onSuccess: (data) => {
      toast.success(`${data.length} Datei(en) ins Archiv hochgeladen.`);
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.archive.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.albums.all });
      onClose();
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Upload fehlgeschlagen.');
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setFiles(Array.from(e.target.files));
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files.length > 0) {
      setFiles(Array.from(e.dataTransfer.files));
    }
  };

  const canSubmit = files.length > 0 && !uploadMutation.isPending;

  return (
    <div
      className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-slate-900">Ins Archiv hochladen</h2>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Schließen"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4 px-6 py-5">
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 py-8 text-center transition-colors hover:border-blue-400 hover:bg-blue-50/30"
          >
            <svg className="h-10 w-10 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
            </svg>
            <p className="mt-2 text-sm font-medium text-slate-700">
              Dateien hier ablegen oder klicken zum Auswählen
            </p>
            <p className="text-xs text-slate-500">
              Bilder, PDFs, Audio, Video · max. 50 MB pro Datei
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileSelect}
            />
          </div>

          {files.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
                {files.length} Datei(en) ausgewählt
              </p>
              <ul className="max-h-32 space-y-1 overflow-y-auto text-sm text-slate-700">
                {files.map((f, i) => (
                  <li key={i} className="flex justify-between gap-2 truncate">
                    <span className="truncate">{f.name}</span>
                    <span className="flex-shrink-0 text-xs text-slate-400">
                      {formatFileSize(f.size)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Kategorie</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as MediaCategory | '')}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <option value="">Automatisch</option>
                {MEDIA_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {MEDIA_CATEGORY_LABELS[c]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Personen (optional)</label>
              <PersonMultiSelect value={persons} onChange={setPersons} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Album (optional)</label>
              <select
                value={albumId}
                onChange={(e) => setAlbumId(e.target.value)}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <option value="">Kein Album</option>
                {albums.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Titel</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="z. B. Hochzeitsfoto 1952"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Datum</label>
              <input
                type="date"
                value={dateTaken}
                onChange={(e) => setDateTaken(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Tags (kommagetrennt)</label>
              <input
                type="text"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="Hochzeit, Schwarzweiß, Kirche"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Ort</label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Berlin, Deutschland"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Quelle</label>
              <input
                type="text"
                value={sourceRef}
                onChange={(e) => setSourceRef(e.target.value)}
                placeholder="z. B. Nachlass Oma Grete"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-medium text-slate-600">Quell-URL</label>
              <input
                type="url"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                placeholder="https://…"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Beschreibung</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              placeholder="Kontext, Personen auf dem Foto, Anlass, ..."
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-6 py-3">
          <button
            onClick={onClose}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Abbrechen
          </button>
          <button
            onClick={() => uploadMutation.mutate()}
            disabled={!canSubmit}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {uploadMutation.isPending ? 'Wird hochgeladen...' : 'Hochladen'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Medien-Vorschau (Preview-Renderer) ──────────────────────────────────────

function MediaPreview({ item, size }: { item: ArchiveMedia; size: 'thumb' | 'full' }) {
  const src = useUploadUrl(item.filename);
  const displayName = item.title || item.originalName || item.filename;
  const category = (item.category ?? 'other') as MediaCategory;

  if (isImage(item.mimeType)) {
    return size === 'full' ? (
      <img src={src} alt={displayName} className="max-h-full max-w-full object-contain" />
    ) : (
      <img
        src={src}
        alt={displayName}
        loading="lazy"
        className="h-full w-full object-cover transition-transform group-hover:scale-105"
      />
    );
  }

  if (isVideo(item.mimeType)) {
    return size === 'full' ? (
      <video src={src} controls className="max-h-full max-w-full" />
    ) : (
      <div className="flex h-full w-full items-center justify-center bg-slate-800 text-white">
        <Film className="h-10 w-10 opacity-80" aria-hidden="true" />
      </div>
    );
  }

  if (isAudio(item.mimeType)) {
    return size === 'full' ? (
      <div className="text-center text-white">
        <Music className="mx-auto mb-3 h-16 w-16 opacity-80" aria-hidden="true" />
        <audio src={src} controls className="mx-auto" />
      </div>
    ) : (
      <div className="flex h-full w-full items-center justify-center bg-slate-700 text-white">
        <Music className="h-10 w-10 opacity-80" aria-hidden="true" />
      </div>
    );
  }

  if (isPdf(item.mimeType)) {
    return size === 'full' ? (
      <iframe
        src={src}
        title={displayName}
        className="h-full w-full rounded bg-white"
      />
    ) : (
      // Für die Grid-Vorschau: PDF einbetten (erste Seite, Fit-to-Width)
      <div className="relative h-full w-full overflow-hidden bg-slate-100">
        <iframe
          src={`${src}#page=1&view=FitH&toolbar=0&navpanes=0`}
          title={displayName}
          className="pointer-events-none h-full w-full"
          tabIndex={-1}
          aria-hidden="true"
        />
        <span className="pointer-events-none absolute bottom-1 right-1 rounded bg-red-600/90 px-1.5 py-0.5 text-[9px] font-semibold text-white">
          PDF
        </span>
      </div>
    );
  }

  return size === 'full' ? (
    <div className="text-center text-white">
      <KategorieIcon category={category} className="mx-auto mb-3 h-16 w-16 text-white/70" />
      <a
        href={src}
        target="_blank"
        rel="noreferrer"
        className="rounded-md bg-white/10 px-4 py-2 text-sm text-white hover:bg-white/20"
      >
        In neuem Tab öffnen
      </a>
    </div>
  ) : (
    <div className="flex h-full w-full flex-col items-center justify-center gap-1">
      <KategorieIcon category={category} className="h-10 w-10 text-slate-400" />
      <span className="px-2 text-[10px] text-slate-400 line-clamp-1">
        {item.mimeType}
      </span>
    </div>
  );
}

// ─── Markierungen ────────────────────────────────────────────────────────────

function neueId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  // Ohne sicheren Kontext (http im LAN) fehlt randomUUID – dann von Hand, v4-förmig.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

const ANZEIGE_FOLGE: MarkierungsAnzeige[] = ['namen', 'punkte', 'aus'];
const ANZEIGE_LABEL: Record<MarkierungsAnzeige, string> = {
  namen: 'Namen',
  punkte: 'Nur Punkte',
  aus: 'Ausgeblendet',
};

// ─── Detail-/Bearbeiten-Modal (mit Gallery-Navigation) ───────────────────────

interface DetailModalProps {
  items: ArchiveMedia[];
  index: number;
  albums: Album[];
  onClose: () => void;
  onNavigate: (newIndex: number) => void;
}

function DetailModal({ items, index, albums, onClose, onNavigate }: DetailModalProps) {
  const item = items[index];
  const queryClient = useQueryClient();
  // Das Recht am Baum entscheidet, nicht die Rolle des Kontos. Wer einen
  // eigenen Stammbaum anlegt, ist dessen Besitzer und darf darin alles – auch
  // wenn sein Konto in der Anlage „Betrachter" heißt. Vorher stand hier
  // `isAdmin`: hochladen ging (das hing schon am Recht), bearbeiten und
  // löschen nicht, und die Knöpfe fehlten kommentarlos.
  const canManage = useAuthStore((s) => s.hasPermission('archive.manage'));
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<UpdateArchiveInput>({
    title: item.title,
    description: item.description,
    date_taken: item.dateTaken ? item.dateTaken.slice(0, 10) : '',
    category: item.category,
    tags: item.tags ?? [],
    sourceRef: item.sourceRef,
    sourceUrl: item.sourceUrl,
    location: item.location,
    personId: item.personId,
    albumId: item.albumId,
  });
  const [tagsInput, setTagsInput] = useState((item.tags ?? []).join(', '));
  const [editPersons, setEditPersons] = useState<PersonRef[]>(item.persons ?? []);

  // Markierungen: wer ist wo auf dem Foto. Bearbeitet wird ein Entwurf, der
  // erst beim Speichern zum Server geht – Abbrechen verwirft alles.
  const istBild = isImage(item.mimeType);
  const gespeicherteMarkierungen = item.markierungen ?? [];
  const [markierModus, setMarkierModus] = useState(false);
  const [entwurf, setEntwurf] = useState<Markierung[]>([]);
  const [anzeige, setAnzeige] = useState<MarkierungsAnzeige>('namen');
  const [aktivId, setAktivId] = useState<string | null>(null);
  const markierungen = markierModus ? entwurf : gespeicherteMarkierungen;

  const personNamen = useMemo(
    () => new Map((item.persons ?? []).map((p) => [p.id, personDisplayName(p)])),
    [item.persons],
  );
  const nameVon = useCallback(
    (m: Markierung) =>
      (m.personId && personNamen.get(m.personId)) || m.name?.trim() || 'Wer ist das?',
    [personNamen],
  );

  // Wenn sich der aktuelle Eintrag durch Navigation ändert, Form-State zurücksetzen
  useEffect(() => {
    setEditing(false);
    setForm({
      title: item.title,
      description: item.description,
      date_taken: item.dateTaken ? item.dateTaken.slice(0, 10) : '',
      category: item.category,
      tags: item.tags ?? [],
      sourceRef: item.sourceRef,
      sourceUrl: item.sourceUrl,
      location: item.location,
      personId: item.personId,
      albumId: item.albumId,
    });
    setTagsInput((item.tags ?? []).join(', '));
    setEditPersons(item.persons ?? []);
    setMarkierModus(false);
    setEntwurf([]);
    setAktivId(null);
  }, [item.id]);

  const markierungMutation = useMutation({
    mutationFn: (liste: Markierung[]) => saveMarkierungen(item.id, liste),
    onSuccess: () => {
      toast.success('Markierungen gespeichert.');
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.archive.all });
      setMarkierModus(false);
      setAktivId(null);
    },
    onError: (err: Error) => toast.error(err.message || 'Speichern fehlgeschlagen.'),
  });

  const starteMarkieren = () => {
    setEditing(false);
    setEntwurf(gespeicherteMarkierungen.map((m) => ({ ...m })));
    setAnzeige('namen');
    setMarkierModus(true);
    setAktivId(null);
  };

  const neueMarkierung = (x: number, y: number) => {
    const m: Markierung = { id: neueId(), x, y, personId: null, name: '', notiz: '' };
    setEntwurf((alt) => [...alt, m]);
    setAktivId(m.id);
  };

  const aendereMarkierung = (id: string, teil: Partial<Markierung>) =>
    setEntwurf((alt) => alt.map((m) => (m.id === id ? { ...m, ...teil } : m)));

  const entferneMarkierung = (id: string) => {
    setEntwurf((alt) => alt.filter((m) => m.id !== id));
    setAktivId(null);
  };

  const speichereMarkierungen = () => {
    const leer = entwurf.filter((m) => !m.personId && !m.name?.trim());
    if (leer.length > 0) {
      setAktivId(leer[0].id);
      toast.error('Bitte jeder Markierung eine Person oder einen Namen geben – oder sie entfernen.');
      return;
    }
    // Von links nach rechts nummerieren – so liest man Gruppenfotos.
    const sortiert = [...entwurf].sort((a, b) => a.x - b.x);
    markierungMutation.mutate(
      sortiert.map((m) => ({
        ...m,
        name: m.name?.trim() || null,
        notiz: m.notiz?.trim() || null,
      })),
    );
  };

  const hasPrev = index > 0;
  const hasNext = index < items.length - 1;

  const goPrev = useCallback(() => {
    if (hasPrev) onNavigate(index - 1);
  }, [hasPrev, index, onNavigate]);
  const goNext = useCallback(() => {
    if (hasNext) onNavigate(index + 1);
  }, [hasNext, index, onNavigate]);

  // Keyboard-Navigation (Pfeil links/rechts + ESC)
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (editing || markierModus) return; // Eingabefelder sollen Pfeile nicht blockieren
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'Escape') {
        onClose();
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [goPrev, goNext, onClose, editing, markierModus]);

  const updateMutation = useMutation({
    mutationFn: (input: UpdateArchiveInput) => updateArchiveItem(item.id, input),
    onSuccess: () => {
      toast.success('Archiv-Eintrag aktualisiert.');
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.archive.all });
      setEditing(false);
    },
    onError: (err: Error) => toast.error(err.message || 'Aktualisierung fehlgeschlagen.'),
  });

  const deleteMutation = useMutation({
    // ID explizit übergeben: Der Handler friert sie vor dem Bestätigungsdialog
    // ein, damit eine Navigation im Hintergrund nicht den Löschkandidaten wechselt.
    mutationFn: (id: string) => deleteArchiveItem(id),
    onSuccess: () => {
      toast.success('Archiv-Eintrag gelöscht.');
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.archive.all });
      onClose();
    },
    onError: (err: Error) => toast.error(err.message || 'Löschen fehlgeschlagen.'),
  });

  const handleSave = () => {
    const tags = tagsInput.split(',').map((t) => t.trim()).filter(Boolean);
    updateMutation.mutate({ ...form, tags, personIds: editPersons.map((p) => p.id) });
  };

  const displayName = item.title || item.originalName || item.filename;
  const category = (item.category ?? 'other') as MediaCategory;

  return (
    <div
      className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="flex h-[90vh] w-full max-w-5xl overflow-hidden rounded-xl bg-white shadow-2xl flex-col md:flex-row"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Vorschau-Bereich mit Gallery-Buttons. Mobil auf 40vh begrenzt, damit
            das Metadaten-Panel samt Bearbeiten-Footer sichtbar bleibt. */}
        <div className="relative flex h-[40vh] shrink-0 items-center justify-center bg-slate-900 p-4 md:h-auto md:min-h-0 md:flex-1">
          {istBild ? (
            <BildMitMarkierungen
              item={item}
              alt={displayName}
              markierungen={markierungen}
              nameVon={nameVon}
              anzeige={anzeige}
              bearbeiten={markierModus}
              aktivId={aktivId}
              onAktiv={setAktivId}
              onNeu={neueMarkierung}
              onVerschiebe={(id, x, y) => aendereMarkierung(id, { x, y })}
            />
          ) : (
            <MediaPreview item={item} size="full" />
          )}

          {/* Bild-Werkzeuge oben links */}
          {istBild && (
            <div className="absolute left-3 top-3 flex flex-wrap items-center gap-1.5">
              {markierModus ? (
                <span className="flex items-center gap-1.5 rounded-full bg-blue-600 px-3 py-1 text-xs font-medium text-white shadow">
                  <MousePointerClick className="h-3.5 w-3.5" aria-hidden="true" />
                  Auf eine Person im Bild klicken · Punkte lassen sich ziehen
                </span>
              ) : (
                <>
                  {gespeicherteMarkierungen.length > 0 && (
                    <button
                      onClick={() =>
                        setAnzeige((a) => ANZEIGE_FOLGE[(ANZEIGE_FOLGE.indexOf(a) + 1) % ANZEIGE_FOLGE.length])
                      }
                      className="flex items-center gap-1.5 rounded-full bg-black/55 px-3 py-1 text-xs font-medium text-white backdrop-blur hover:bg-black/70"
                      title="Markierungen: Namen · nur Punkte · ausgeblendet"
                    >
                      {anzeige === 'aus' ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      {ANZEIGE_LABEL[anzeige]}
                    </button>
                  )}
                  {canManage && (
                    <button
                      onClick={starteMarkieren}
                      className="flex items-center gap-1.5 rounded-full bg-black/55 px-3 py-1 text-xs font-medium text-white backdrop-blur hover:bg-black/70"
                      title="Markieren, wer auf dem Foto zu sehen ist – das Bild selbst bleibt unverändert"
                    >
                      <Tag className="h-3.5 w-3.5" />
                      Personen markieren
                    </button>
                  )}
                  <OriginalLink filename={item.filename} />
                </>
              )}
            </div>
          )}

          {/* Pfeil links */}
          {hasPrev && !markierModus && (
            <button
              onClick={goPrev}
              className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white backdrop-blur hover:bg-black/70"
              aria-label="Vorheriges"
              title="Vorheriges (←)"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
              </svg>
            </button>
          )}

          {/* Pfeil rechts */}
          {hasNext && !markierModus && (
            <button
              onClick={goNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white backdrop-blur hover:bg-black/70"
              aria-label="Nächstes"
              title="Nächstes (→)"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
              </svg>
            </button>
          )}

          {/* Positionsanzeige */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs text-white backdrop-blur">
            {index + 1} / {items.length}
          </div>
        </div>

        {/* Metadaten-Panel. Mobil als beschränktes Flex-Kind (min-h-0), damit der
            innere Scrollbereich greift und der Footer unten sichtbar bleibt. */}
        <div className="flex w-full min-h-0 flex-1 flex-col bg-white md:w-96 md:flex-none md:border-l md:border-slate-200">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <h2 className="truncate text-base font-semibold text-slate-900">{displayName}</h2>
            <button
              onClick={onClose}
              className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              aria-label="Schließen"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 text-sm">
            {markierModus ? (
              <MarkierungsEditor
                entwurf={entwurf}
                nameVon={nameVon}
                aktivId={aktivId}
                onAktiv={setAktivId}
                onAendere={aendereMarkierung}
                onEntferne={entferneMarkierung}
              />
            ) : !editing ? (
              <dl className="space-y-3">
                {istBild && gespeicherteMarkierungen.length > 0 && (
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                      Auf diesem Foto <span className="font-normal normal-case tracking-normal">(von links)</span>
                    </dt>
                    <dd className="mt-1">
                      <ol className="space-y-0.5">
                        {gespeicherteMarkierungen.map((m, i) => (
                          <li
                            key={m.id}
                            onMouseEnter={() => setAktivId(m.id)}
                            onMouseLeave={() => setAktivId(null)}
                            className={`flex gap-2 rounded-md px-1.5 py-1 ${aktivId === m.id ? 'bg-blue-50' : ''}`}
                          >
                            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-slate-700 text-[9px] font-bold text-white">
                              {i + 1}
                            </span>
                            <span className="min-w-0">
                              {m.personId && personNamen.has(m.personId) ? (
                                <Link
                                  to={`/?person=${encodeURIComponent(m.personId)}`}
                                  className="font-medium text-slate-800 hover:text-blue-700 hover:underline"
                                  title="Im Stammbaum zeigen"
                                >
                                  {nameVon(m)}
                                </Link>
                              ) : (
                                <span className="font-medium text-slate-800">{nameVon(m)}</span>
                              )}
                              {m.notiz && <span className="block text-xs text-slate-500">{m.notiz}</span>}
                            </span>
                          </li>
                        ))}
                      </ol>
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wider text-slate-400">Kategorie</dt>
                  <dd className="mt-0.5 flex items-center gap-1.5 text-slate-800">
                    <KategorieIcon category={category} className="h-4 w-4 text-slate-500" />
                    {MEDIA_CATEGORY_LABELS[category]}
                  </dd>
                </div>
                {item.dateTaken && (
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wider text-slate-400">Datum</dt>
                    <dd className="mt-0.5 text-slate-800">{formatDate(item.dateTaken)}</dd>
                  </div>
                )}
                {item.description && (
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wider text-slate-400">Beschreibung</dt>
                    <dd className="mt-0.5 whitespace-pre-wrap text-slate-800">{item.description}</dd>
                  </div>
                )}
                {item.persons.length > 0 && (
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                      {item.persons.length === 1 ? 'Person' : 'Personen'}
                    </dt>
                    <dd className="mt-0.5 text-slate-800">
                      {item.persons.map((p) => personDisplayName(p)).join(', ')}
                    </dd>
                  </div>
                )}
                {item.album && (
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wider text-slate-400">Album</dt>
                    <dd className="mt-0.5 text-slate-800">{item.album.name}</dd>
                  </div>
                )}
                {item.location && (
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wider text-slate-400">Ort</dt>
                    <dd className="mt-0.5 text-slate-800">{item.location}</dd>
                  </div>
                )}
                {item.sourceRef && (
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wider text-slate-400">Quelle</dt>
                    <dd className="mt-0.5 text-slate-800">{item.sourceRef}</dd>
                  </div>
                )}
                {item.sourceUrl && (
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wider text-slate-400">Quell-URL</dt>
                    <dd className="mt-0.5">
                      <a
                        href={item.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="break-all text-blue-600 underline hover:text-blue-800"
                      >
                        {item.sourceUrl}
                      </a>
                    </dd>
                  </div>
                )}
                {item.tags && item.tags.length > 0 && (
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wider text-slate-400">Tags</dt>
                    <dd className="mt-1 flex flex-wrap gap-1">
                      {item.tags.map((t) => (
                        <span
                          key={t}
                          className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700"
                        >
                          {t}
                        </span>
                      ))}
                    </dd>
                  </div>
                )}
                <div className="border-t border-slate-100 pt-3">
                  <dt className="text-xs font-semibold uppercase tracking-wider text-slate-400">Datei</dt>
                  <dd className="mt-0.5 space-y-0.5 text-xs text-slate-500">
                    <div>{item.originalName ?? item.filename}</div>
                    <div>{item.mimeType} · {formatFileSize(item.fileSize)}</div>
                    <div>Hinzugefügt am {formatDate(item.createdAt)}</div>
                  </dd>
                </div>
              </dl>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Titel</label>
                  <input
                    type="text"
                    value={form.title ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Kategorie</label>
                  <select
                    value={form.category ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as MediaCategory }))}
                    className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm"
                  >
                    {MEDIA_CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {MEDIA_CATEGORY_LABELS[c]}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Datum</label>
                  <input
                    type="date"
                    value={form.date_taken ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, date_taken: e.target.value }))}
                    className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Personen</label>
                  <PersonMultiSelect value={editPersons} onChange={setEditPersons} />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Album</label>
                  <select
                    value={form.albumId ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, albumId: e.target.value || null }))}
                    className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm"
                  >
                    <option value="">Kein Album</option>
                    {albums.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Ort</label>
                  <input
                    type="text"
                    value={form.location ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                    className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Quelle</label>
                  <input
                    type="text"
                    value={form.sourceRef ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, sourceRef: e.target.value }))}
                    className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Quell-URL</label>
                  <input
                    type="url"
                    value={form.sourceUrl ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, sourceUrl: e.target.value }))}
                    placeholder="https://…"
                    className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Tags (kommagetrennt)</label>
                  <input
                    type="text"
                    value={tagsInput}
                    onChange={(e) => setTagsInput(e.target.value)}
                    className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Beschreibung</label>
                  <textarea
                    value={form.description ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    rows={4}
                    className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                  />
                </div>
              </div>
            )}
          </div>

          {canManage && markierModus && (
            <div className="flex items-center justify-between gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
              <button
                onClick={() => {
                  setMarkierModus(false);
                  setAktivId(null);
                }}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                Abbrechen
              </button>
              <button
                onClick={speichereMarkierungen}
                disabled={markierungMutation.isPending}
                className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {markierungMutation.isPending ? 'Wird gespeichert...' : 'Markierungen speichern'}
              </button>
            </div>
          )}

          {canManage && !markierModus && (
            <div className="flex items-center justify-between gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
              {editing ? (
                <>
                  <button
                    onClick={() => setEditing(false)}
                    className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={updateMutation.isPending}
                    className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {updateMutation.isPending ? 'Wird gespeichert...' : 'Speichern'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={async () => {
                      // ID vor dem await einfrieren: Falls sich der angezeigte
                      // Eintrag während des offenen Dialogs ändert, wird trotzdem
                      // genau der bestätigte Eintrag gelöscht.
                      const idToDelete = item.id;
                      const ok = await confirm({
                        title: 'Archiv-Eintrag löschen?',
                        message: (
                          <>
                            <strong>&quot;{displayName}&quot;</strong> wird unwiderruflich gelöscht.
                          </>
                        ),
                        confirmLabel: 'Löschen',
                        variant: 'danger',
                      });
                      if (ok) deleteMutation.mutate(idToDelete);
                    }}
                    className="rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                  >
                    Löschen
                  </button>
                  <button
                    onClick={() => setEditing(true)}
                    className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
                  >
                    Bearbeiten
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Bild mit Markierungs-Ebene ──────────────────────────────────────────────

function BildMitMarkierungen(props: Omit<React.ComponentProps<typeof FotoMarkierungen>, 'src'> & { item: ArchiveMedia }) {
  const { item, ...rest } = props;
  // Immer die Originaldatei – keine Vorschau, keine verkleinerte Fassung.
  const src = useUploadUrl(item.filename);
  return <FotoMarkierungen src={src} {...rest} />;
}

function OriginalLink({ filename }: { filename: string }) {
  const src = useUploadUrl(filename);
  if (!src) return null;
  return (
    <a
      href={src}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-1.5 rounded-full bg-black/55 px-3 py-1 text-xs font-medium text-white backdrop-blur hover:bg-black/70"
      title="Originaldatei in voller Auflösung öffnen"
    >
      <ExternalLink className="h-3.5 w-3.5" />
      Original
    </a>
  );
}

function MarkierungsEditor({
  entwurf,
  nameVon,
  aktivId,
  onAktiv,
  onAendere,
  onEntferne,
}: {
  entwurf: Markierung[];
  nameVon: (m: Markierung) => string;
  aktivId: string | null;
  onAktiv: (id: string | null) => void;
  onAendere: (id: string, teil: Partial<Markierung>) => void;
  onEntferne: (id: string) => void;
}) {
  const aktivRef = useRef<HTMLLIElement>(null);
  useEffect(() => {
    aktivRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [aktivId]);

  return (
    <div className="space-y-3">
      <div className="rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-800">
        <p className="font-medium">Wer ist auf dem Foto?</p>
        <p className="mt-0.5 text-blue-700">
          Klicke im Bild auf eine Person, um sie zu markieren. Das Foto selbst wird dabei nicht verändert – die Pfeile
          liegen als eigene Ebene darüber und lassen sich jederzeit ausblenden.
        </p>
      </div>

      {entwurf.length === 0 ? (
        <p className="flex flex-col items-center gap-1.5 py-6 text-center text-xs text-slate-400">
          <Users className="h-5 w-5" aria-hidden="true" />
          Noch niemand markiert.
        </p>
      ) : (
        <ol className="space-y-2">
          {entwurf.map((m, i) => {
            const aktiv = aktivId === m.id;
            return (
              <li
                key={m.id}
                ref={aktiv ? aktivRef : undefined}
                onClick={() => onAktiv(m.id)}
                className={`rounded-lg border p-2.5 transition-colors ${
                  aktiv ? 'border-blue-300 bg-blue-50/60' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white ${
                      aktiv ? 'bg-blue-600' : 'bg-slate-700'
                    }`}
                  >
                    {i + 1}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">
                    {m.personId || m.name?.trim() ? nameVon(m) : <span className="text-slate-400">Noch ohne Namen</span>}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEntferne(m.id);
                    }}
                    className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                    aria-label="Markierung entfernen"
                    title="Markierung entfernen"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>

                {aktiv && (
                  <div className="mt-2 space-y-2" onClick={(e) => e.stopPropagation()}>
                    <div>
                      <label className="mb-1 block text-[11px] font-medium text-slate-500">Person aus dem Stammbaum</label>
                      <PersonAutocomplete
                        value={m.personId ?? null}
                        onChange={(pid) => onAendere(m.id, { personId: pid })}
                        onSelectPerson={(p) =>
                          onAendere(m.id, {
                            personId: p.id,
                            name: `${p.firstName} ${p.lastName ?? ''}`.trim(),
                          })
                        }
                        placeholder="Name eingeben…"
                        aria-label="Person aus dem Stammbaum"
                      />
                    </div>
                    {!m.personId && (
                      <div>
                        <label className="mb-1 block text-[11px] font-medium text-slate-500">
                          … oder freier Name (nicht im Stammbaum)
                        </label>
                        <input
                          type="text"
                          value={m.name ?? ''}
                          maxLength={200}
                          onChange={(e) => onAendere(m.id, { name: e.target.value })}
                          placeholder="z. B. Nachbarin, unbekannter Soldat"
                          className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                        />
                      </div>
                    )}
                    <div>
                      <label className="mb-1 block text-[11px] font-medium text-slate-500">Beschreibung (optional)</label>
                      <input
                        type="text"
                        value={m.notiz ?? ''}
                        maxLength={500}
                        onChange={(e) => onAendere(m.id, { notiz: e.target.value })}
                        placeholder="z. B. mit Hut, ca. 12 Jahre alt"
                        className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                      />
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

// ─── Archiv-Kachel mit Checkbox ──────────────────────────────────────────────

interface ArchiveCardProps {
  item: ArchiveMedia;
  onClick: () => void;
  selected: boolean;
  onToggleSelect: () => void;
  selectionMode: boolean;
}

function ArchiveCard({ item, onClick, selected, onToggleSelect, selectionMode }: ArchiveCardProps) {
  const category = (item.category ?? 'other') as MediaCategory;
  const displayName = item.title || item.originalName || item.filename;

  return (
    <div
      className={`group relative flex flex-col overflow-hidden rounded-lg border bg-white text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${
        selected ? 'border-blue-500 ring-2 ring-blue-300' : 'border-slate-200'
      }`}
    >
      {/* Checkbox (oben links) */}
      <label
        className={`absolute left-1.5 top-1.5 z-10 flex h-6 w-6 cursor-pointer items-center justify-center rounded-md border bg-white/90 shadow-sm transition ${
          selectionMode || selected
            ? 'opacity-100'
            : 'opacity-0 group-hover:opacity-100'
        } ${selected ? 'border-blue-500' : 'border-slate-300'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          className="sr-only"
          aria-label={`${displayName} auswählen`}
        />
        {selected && (
          <svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
          </svg>
        )}
      </label>

      <button
        onClick={onClick}
        className="flex flex-1 flex-col text-left focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <div className="relative aspect-[4/3] w-full overflow-hidden bg-slate-100">
          <MediaPreview item={item} size="thumb" />
          <span className="absolute left-1.5 bottom-1.5 flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur">
            <KategorieIcon category={category} className="h-3 w-3" />
            {MEDIA_CATEGORY_LABELS[category]}
          </span>
          {item.persons.length === 0 && (
            <span className="absolute right-1.5 top-1.5 rounded-full bg-amber-500/90 px-2 py-0.5 text-[10px] font-medium text-white">
              nicht zugeordnet
            </span>
          )}
          {(item.markierungen?.length ?? 0) > 0 && (
            <span
              className="absolute bottom-1.5 right-1.5 flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur"
              title={`${item.markierungen!.length} Person(en) auf dem Foto markiert`}
            >
              <Tag className="h-2.5 w-2.5" aria-hidden="true" />
              {item.markierungen!.length}
            </span>
          )}
        </div>
        <div className="flex flex-1 flex-col gap-1 p-2.5">
          <div className="truncate text-sm font-medium text-slate-800" title={displayName}>
            {displayName}
          </div>
          <div className="flex items-center justify-between gap-2 text-[11px] text-slate-500">
            <span className="truncate">
              {item.album
                ? item.album.name
                : item.persons.length > 0
                  ? item.persons.map((p) => personDisplayName(p)).join(', ')
                  : 'Allgemein'}
            </span>
            <span className="flex-shrink-0">{formatDate(item.dateTaken ?? item.createdAt)}</span>
          </div>
          {item.tags && item.tags.length > 0 && (
            <div className="mt-0.5 flex flex-wrap gap-1">
              {item.tags.slice(0, 3).map((t) => (
                <span
                  key={t}
                  className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600"
                >
                  {t}
                </span>
              ))}
              {item.tags.length > 3 && (
                <span className="text-[10px] text-slate-400">+{item.tags.length - 3}</span>
              )}
            </div>
          )}
        </div>
      </button>
    </div>
  );
}

// ─── Batch-Toolbar ──────────────────────────────────────────────────────────

interface BatchToolbarProps {
  selectedIds: string[];
  albums: Album[];
  onClear: () => void;
  onDone: () => void;
}

function BatchToolbar({ selectedIds, albums, onClear, onDone }: BatchToolbarProps) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'none' | 'tags' | 'category' | 'date' | 'person' | 'album'>('none');
  const [tagsToAdd, setTagsToAdd] = useState('');
  const [category, setCategory] = useState<MediaCategory | ''>('');
  const [dateValue, setDateValue] = useState('');
  const [personId, setPersonId] = useState('');
  const [albumId, setAlbumId] = useState('');

  const batchMutation = useMutation({
    mutationFn: batchUpdateArchive,
    onSuccess: (data) => {
      toast.success(`${data.affected} Eintrag/Einträge aktualisiert.`);
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.archive.all });
      setMode('none');
      setTagsToAdd('');
      setCategory('');
      setDateValue('');
      setPersonId('');
      setAlbumId('');
      onDone();
    },
    onError: (err: Error) => toast.error(err.message || 'Stapelaktion fehlgeschlagen.'),
  });

  const applyTags = () => {
    const tags = tagsToAdd.split(',').map((t) => t.trim()).filter(Boolean);
    if (tags.length === 0) {
      toast.error('Bitte mindestens einen Tag eingeben.');
      return;
    }
    batchMutation.mutate({ ids: selectedIds, tagsAdd: tags });
  };

  const applyCategory = () => {
    if (!category) {
      toast.error('Bitte eine Kategorie wählen.');
      return;
    }
    batchMutation.mutate({ ids: selectedIds, category });
  };

  const applyDate = () => {
    if (!dateValue) {
      toast.error('Bitte ein Datum eingeben.');
      return;
    }
    batchMutation.mutate({ ids: selectedIds, date_taken: dateValue });
  };

  const applyPerson = () => {
    batchMutation.mutate({ ids: selectedIds, personId: personId || null });
  };

  const applyAlbum = () => {
    batchMutation.mutate({ ids: selectedIds, albumId: albumId || null });
  };

  return (
    <div className="sticky top-0 z-30 flex flex-wrap items-center gap-2 rounded-lg border border-blue-300 bg-blue-50 px-3 py-2 shadow-sm">
      <span className="text-sm font-medium text-blue-900">
        {selectedIds.length} ausgewählt
      </span>

      <div className="h-4 w-px bg-blue-300" aria-hidden="true" />

      {mode === 'none' && (
        <>
          <button
            onClick={() => setMode('tags')}
            className="flex items-center gap-1.5 rounded-md border border-blue-300 bg-white px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
          >
            <Tags className="h-3.5 w-3.5" aria-hidden="true" />
            Tags hinzufügen
          </button>
          <button
            onClick={() => setMode('category')}
            className="flex items-center gap-1.5 rounded-md border border-blue-300 bg-white px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
          >
            <FolderInput className="h-3.5 w-3.5" aria-hidden="true" />
            Kategorie setzen
          </button>
          <button
            onClick={() => setMode('date')}
            className="flex items-center gap-1.5 rounded-md border border-blue-300 bg-white px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
          >
            <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
            Datum setzen
          </button>
          <button
            onClick={() => setMode('person')}
            className="flex items-center gap-1.5 rounded-md border border-blue-300 bg-white px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
          >
            <UserPlus className="h-3.5 w-3.5" aria-hidden="true" />
            Person zuordnen
          </button>
          <button
            onClick={() => setMode('album')}
            className="flex items-center gap-1.5 rounded-md border border-blue-300 bg-white px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
          >
            <FolderOpen className="h-3.5 w-3.5" aria-hidden="true" />
            Album zuordnen
          </button>
        </>
      )}

      {mode === 'tags' && (
        <>
          <input
            type="text"
            value={tagsToAdd}
            onChange={(e) => setTagsToAdd(e.target.value)}
            placeholder="Tags (kommagetrennt)"
            className="rounded-md border border-slate-300 px-2 py-1 text-xs"
            autoFocus
          />
          <button
            onClick={applyTags}
            disabled={batchMutation.isPending}
            className="rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Hinzufügen
          </button>
          <button onClick={() => setMode('none')} className="text-xs text-slate-600 hover:text-slate-800">
            Abbrechen
          </button>
        </>
      )}

      {mode === 'category' && (
        <>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as MediaCategory | '')}
            className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs"
            autoFocus
          >
            <option value="">Kategorie wählen …</option>
            {MEDIA_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {MEDIA_CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
          <button
            onClick={applyCategory}
            disabled={batchMutation.isPending}
            className="rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Setzen
          </button>
          <button onClick={() => setMode('none')} className="text-xs text-slate-600 hover:text-slate-800">
            Abbrechen
          </button>
        </>
      )}

      {mode === 'date' && (
        <>
          <input
            type="date"
            value={dateValue}
            onChange={(e) => setDateValue(e.target.value)}
            className="rounded-md border border-slate-300 px-2 py-1 text-xs"
            autoFocus
          />
          <button
            onClick={applyDate}
            disabled={batchMutation.isPending}
            className="rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Setzen
          </button>
          <button onClick={() => setMode('none')} className="text-xs text-slate-600 hover:text-slate-800">
            Abbrechen
          </button>
        </>
      )}

      {mode === 'person' && (
        <>
          <div className="min-w-[240px]">
            <PersonAutocomplete
              value={personId || null}
              onChange={(id) => setPersonId(id ?? '')}
              placeholder="Person suchen..."
            />
          </div>
          <button
            onClick={applyPerson}
            disabled={batchMutation.isPending}
            className="rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Zuordnen
          </button>
          <button onClick={() => setMode('none')} className="text-xs text-slate-600 hover:text-slate-800">
            Abbrechen
          </button>
        </>
      )}

      {mode === 'album' && (
        <>
          <select
            value={albumId}
            onChange={(e) => setAlbumId(e.target.value)}
            className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs"
            autoFocus
          >
            <option value="">Kein Album</option>
            {albums.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          <button
            onClick={applyAlbum}
            disabled={batchMutation.isPending}
            className="rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Zuordnen
          </button>
          <button onClick={() => setMode('none')} className="text-xs text-slate-600 hover:text-slate-800">
            Abbrechen
          </button>
        </>
      )}

      <div className="flex-1" />

      <button
        onClick={onClear}
        className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-50"
      >
        Auswahl aufheben
      </button>
    </div>
  );
}

// ─── Stats-Übersicht ─────────────────────────────────────────────────────────

function StatsOverview({ stats }: { stats: ArchiveStats }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <div className="rounded-lg border border-slate-200 bg-white p-3">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Gesamt</p>
        <p className="mt-0.5 text-2xl font-semibold text-slate-800">{stats.total}</p>
      </div>
      <div className="rounded-lg border border-slate-200 bg-white p-3">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Nicht zugeordnet</p>
        <p className="mt-0.5 text-2xl font-semibold text-amber-600">{stats.unassigned}</p>
      </div>
      <div className="col-span-2 rounded-lg border border-slate-200 bg-white p-3 sm:col-span-2">
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Kategorien</p>
        <div className="flex flex-wrap gap-1.5">
          {stats.byCategory.slice(0, 6).map((c) => {
            const cat = (c.category as MediaCategory) || 'other';
            return (
              <span
                key={c.category}
                className="flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700"
              >
                <KategorieIcon category={cat} className="h-3.5 w-3.5 text-slate-500" />
                {MEDIA_CATEGORY_LABELS[cat] ?? c.category}
                <span className="font-semibold">{c.count}</span>
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Seite ───────────────────────────────────────────────────────────────────

const PAGE_SIZE = 48;

// ─── Album-Bearbeiten Modal ─────────────────────────────────────────────────

function EditAlbumModal({ album, onClose }: { album: Album; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(album.name);
  const [description, setDescription] = useState(album.description ?? '');

  const mutation = useMutation({
    mutationFn: () =>
      updateAlbum(album.id, {
        name: name.trim(),
        description: description.trim() || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.albums.all });
      toast.success('Album aktualisiert.');
      onClose();
    },
    onError: (err: Error) => toast.error(err.message || 'Aktualisieren fehlgeschlagen.'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-4 text-lg font-semibold text-slate-900">Album bearbeiten</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
          className="space-y-4"
        >
          <div>
            <label htmlFor="albumName" className="mb-1 block text-sm font-medium text-slate-700">
              Name
            </label>
            <input
              id="albumName"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={200}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label htmlFor="albumDesc" className="mb-1 block text-sm font-medium text-slate-700">
              Beschreibung (optional)
            </label>
            <textarea
              id="albumDesc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {mutation.isPending ? 'Speichert...' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Album-Karte ────────────────────────────────────────────────────────────

interface AlbumCardProps {
  album: Album;
  onOpen: () => void;
  onEdit: () => void;
  onDelete: () => void;
  canManage: boolean;
}

function AlbumCard({ album, onOpen, onEdit, onDelete, canManage }: AlbumCardProps) {
  const hasImage =
    album.previewFilename && album.previewMimeType?.startsWith('image/');
  const previewSrc = useUploadUrl(album.previewFilename);

  return (
    <div className="group relative overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition hover:shadow-md">
      <button
        type="button"
        onClick={onOpen}
        className="flex aspect-[4/3] w-full items-center justify-center bg-slate-100 text-5xl text-slate-400"
      >
        {hasImage && previewSrc ? (
          <img
            src={previewSrc}
            alt={album.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
          />
        ) : (
          <FolderOpen className="h-10 w-10 text-slate-300" aria-hidden="true" />
        )}
      </button>
      <div className="flex items-start justify-between gap-2 p-3">
        <button
          type="button"
          onClick={onOpen}
          className="min-w-0 flex-1 text-left"
        >
          <p className="truncate text-sm font-semibold text-slate-900">{album.name}</p>
          {album.description && (
            <p className="line-clamp-2 text-xs text-slate-500">{album.description}</p>
          )}
          <p className="mt-1 text-xs text-slate-400">
            {album.mediaCount} {album.mediaCount === 1 ? 'Medium' : 'Medien'}
          </p>
        </button>
        {canManage && (
          <div className="flex shrink-0 flex-col gap-1">
            <button
              type="button"
              onClick={onEdit}
              className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              title="Bearbeiten"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" />
              </svg>
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="rounded-md p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
              title="Löschen"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Lebensläufe-Bereich (Liste + Personen-Suche) ───────────────────────────

function BiographiesSection({ onOpen }: { onOpen: (personId: string) => void }) {
  const { data: biographies, isLoading } = useQuery({
    queryKey: CAREER_QUERY_KEYS.biographyList,
    queryFn: listBiographies,
  });

  return (
    <div className="space-y-4">
      {/* Personen-Suche: öffnet den Lebenslauf JEDER Person */}
      <div className="rounded-lg border border-slate-200 bg-white p-3">
        <label className="mb-1 block text-xs font-medium text-slate-600">
          Lebenslauf einer Person öffnen
        </label>
        <div className="max-w-md">
          <PersonAutocomplete
            value={null}
            onChange={(id) => {
              if (id) onOpen(id);
            }}
            placeholder="Person suchen..."
            aria-label="Person für Lebenslauf suchen"
          />
        </div>
        <p className="mt-1.5 text-xs text-slate-400">
          Auch ohne gepflegten Erzähltext hat jede Person eine automatisch generierte Chronik.
        </p>
      </div>

      {/* Liste aller gepflegten Lebensläufe */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : !biographies || biographies.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white py-16 text-center">
          <ScrollText className="h-12 w-12 text-slate-300" aria-hidden="true" />
          <h2 className="mt-3 text-lg font-semibold text-slate-800">Noch keine Lebensläufe</h2>
          <p className="mt-1 max-w-sm text-sm text-slate-500">
            Suche oben eine Person, um ihre Chronik zu sehen und einen Erzähltext zu pflegen.
          </p>
        </div>
      ) : (
        <>
          <p className="text-xs text-slate-500">
            {biographies.length === 1
              ? '1 gepflegter Lebenslauf'
              : `${biographies.length} gepflegte Lebensläufe`}
          </p>
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <ul className="divide-y divide-slate-100">
              {biographies.map((b) => {
                const lifeDates = [
                  b.person.birthDateText ? `* ${b.person.birthDateText}` : null,
                  b.person.deathDateText ? `† ${b.person.deathDateText}` : null,
                ]
                  .filter(Boolean)
                  .join(' · ');
                return (
                  <li key={b.id}>
                    <button
                      type="button"
                      onClick={() => onOpen(b.personId)}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50"
                    >
                      <ScrollText className="h-4 w-4 flex-shrink-0 text-slate-400" aria-hidden="true" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-slate-800">
                          {[b.person.firstName, b.person.lastName].filter(Boolean).join(' ')}
                        </p>
                        {lifeDates && <p className="text-xs text-slate-500">{lifeDates}</p>}
                      </div>
                      <span className="flex-shrink-0 text-xs text-slate-400">
                        zuletzt bearbeitet {formatDate(b.updatedAt)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

export function ArchivePage() {
  const canManageArchive = useAuthStore((s) => s.hasPermission('archive.manage'));
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  // ?bio=<personId> → Lebensläufe-Bereich mit geöffneter Detailansicht (Deep-Link)
  const [searchParams, setSearchParams] = useSearchParams();
  const bioPersonId = searchParams.get('bio');

  const [view, setView] = useState<'media' | 'albums' | 'bios'>(
    bioPersonId ? 'bios' : 'media',
  );
  const [editAlbum, setEditAlbum] = useState<Album | null>(null);

  // Deep-Link auch bei späterer Param-Änderung (z. B. Browser-Zurück) respektieren
  useEffect(() => {
    if (bioPersonId) setView('bios');
  }, [bioPersonId]);

  const openBiography = useCallback(
    (personId: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('bio', personId);
        return next;
      });
    },
    [setSearchParams],
  );

  const closeBiography = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('bio');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [category, setCategory] = useState<MediaCategory | ''>('');
  const [personId, setPersonId] = useState<string>('');
  const [albumFilter, setAlbumFilter] = useState<string>(''); // '', 'none', oder UUID
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [activeTag, setActiveTag] = useState<string>('');

  const [showUpload, setShowUpload] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Debounce Suche
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const filterParams = useMemo(
    () => ({
      q: debouncedSearch || undefined,
      category: category || undefined,
      personId: personId || undefined,
      albumId: albumFilter || undefined,
      unassigned: unassignedOnly || undefined,
      tag: activeTag || undefined,
    }),
    [debouncedSearch, category, personId, albumFilter, unassignedOnly, activeTag],
  );

  // Infinite Query für Archiv-Liste
  const {
    data: pageData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: QUERY_KEYS.archive.list(filterParams),
    queryFn: ({ pageParam = 1 }) =>
      fetchArchive({ ...filterParams, page: pageParam as number, limit: PAGE_SIZE }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce((sum, p) => sum + p.data.length, 0);
      return loadedCount < lastPage.total ? allPages.length + 1 : undefined;
    },
  });

  const items = useMemo(
    () => pageData?.pages.flatMap((p) => p.data) ?? [],
    [pageData],
  );
  const total = pageData?.pages[0]?.total ?? 0;

  // IntersectionObserver für Infinite Scroll
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '200px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Auswahl zurücksetzen, wenn sich die Filter ändern
  useEffect(() => {
    setSelectedIds(new Set());
  }, [filterParams]);

  const { data: stats } = useQuery({
    queryKey: QUERY_KEYS.archive.stats,
    queryFn: fetchArchiveStats,
  });

  const { data: albums = [] } = useQuery({
    queryKey: QUERY_KEYS.albums.all,
    queryFn: fetchAlbums,
  });

  const resetFilters = useCallback(() => {
    setSearch('');
    setCategory('');
    setPersonId('');
    setAlbumFilter('');
    setUnassignedOnly(false);
    setActiveTag('');
  }, []);

  const hasActiveFilter =
    !!debouncedSearch ||
    !!category ||
    !!personId ||
    !!albumFilter ||
    unassignedOnly ||
    !!activeTag;

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleCreateAlbum = async () => {
    const name = window.prompt('Name des neuen Albums:');
    if (!name?.trim()) return;
    try {
      await createAlbum({ name: name.trim() });
      toast.success('Album angelegt.');
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.albums.all });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Album konnte nicht angelegt werden.');
    }
  };

  const handleDeleteAlbum = async (album: Album) => {
    const ok = await confirm({
      title: 'Album löschen?',
      message: (
        <>
          Album <strong>"{album.name}"</strong> wirklich löschen? Die enthaltenen Medien
          bleiben erhalten.
        </>
      ),
      confirmLabel: 'Löschen',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteAlbum(album.id);
      toast.success('Album gelöscht.');
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.albums.all });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Album konnte nicht gelöscht werden.');
    }
  };

  const openAlbumInMedia = (album: Album) => {
    setAlbumFilter(album.id);
    setView('media');
  };

  const selectionArray = useMemo(() => Array.from(selectedIds), [selectedIds]);

  return (
    <AppLayout>
      <div className="mx-auto w-full max-w-7xl space-y-4 px-4 py-5 sm:px-6">
        {/* Kopf */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-semibold text-slate-900">
              <Archive className="h-6 w-6 text-slate-500" aria-hidden="true" /> Archiv
            </h1>
            <p className="text-sm text-slate-500">
              Dokumente, Fotos und andere Familienschätze – klassifiziert und durchsuchbar.
            </p>
          </div>
          {canManageArchive && view !== 'bios' && (
            <div className="flex gap-2">
              {view === 'albums' ? (
                <button
                  onClick={handleCreateAlbum}
                  className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                  Neues Album
                </button>
              ) : (
                <>
                  <button
                    onClick={handleCreateAlbum}
                    className="flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
                  >
                    <FolderPlus className="h-4 w-4" aria-hidden="true" />
                    Neues Album
                  </button>
                  <button
                    onClick={() => setShowUpload(true)}
                    className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    Hochladen
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* View-Toggle */}
        <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5 shadow-sm">
          <button
            type="button"
            onClick={() => {
              setView('media');
              if (bioPersonId) closeBiography();
            }}
            className={`flex items-center gap-1.5 rounded px-3 py-1.5 text-sm font-medium transition ${
              view === 'media'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <FolderOpen className="h-4 w-4" aria-hidden="true" />
            Medien
          </button>
          <button
            type="button"
            onClick={() => {
              setView('albums');
              if (bioPersonId) closeBiography();
            }}
            className={`flex items-center gap-1.5 rounded px-3 py-1.5 text-sm font-medium transition ${
              view === 'albums'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Images className="h-4 w-4" aria-hidden="true" />
            Alben {albums.length > 0 && `(${albums.length})`}
          </button>
          <button
            type="button"
            onClick={() => setView('bios')}
            className={`flex items-center gap-1.5 rounded px-3 py-1.5 text-sm font-medium transition ${
              view === 'bios'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ScrollText className="h-4 w-4" aria-hidden="true" />
            Lebensläufe
          </button>
        </div>

        {view === 'bios' ? (
          bioPersonId ? (
            <BiographyView
              key={bioPersonId}
              personId={bioPersonId}
              onBack={closeBiography}
            />
          ) : (
            <BiographiesSection onOpen={openBiography} />
          )
        ) : view === 'albums' ? (
          <>
            {albums.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white py-16 text-center">
                <FolderOpen className="h-12 w-12 text-slate-300" aria-hidden="true" />
                <h2 className="mt-3 text-lg font-semibold text-slate-800">Noch keine Alben</h2>
                <p className="mt-1 max-w-sm text-sm text-slate-500">
                  Gruppiere deine Medien in Alben, um sie besser zu organisieren.
                </p>
                {canManageArchive && (
                  <button
                    onClick={handleCreateAlbum}
                    className="mt-4 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                  >
                    Erstes Album anlegen
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {albums.map((a) => (
                  <AlbumCard
                    key={a.id}
                    album={a}
                    canManage={canManageArchive}
                    onOpen={() => openAlbumInMedia(a)}
                    onEdit={() => setEditAlbum(a)}
                    onDelete={() => handleDeleteAlbum(a)}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <>

        {/* Batch-Toolbar */}
        {selectionArray.length > 0 && canManageArchive && (
          <BatchToolbar
            selectedIds={selectionArray}
            albums={albums}
            onClear={() => setSelectedIds(new Set())}
            onDone={() => setSelectedIds(new Set())}
          />
        )}

        {/* Stats */}
        {stats && <StatsOverview stats={stats} />}

        {/* Filter-Zeile */}
        <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-3">
          <div className="flex flex-wrap gap-2">
            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Suchen in Titel, Beschreibung, Ort ..."
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </div>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as MediaCategory | '')}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">Alle Kategorien</option>
              {MEDIA_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {MEDIA_CATEGORY_LABELS[c]}
                </option>
              ))}
            </select>
            <div className="min-w-[240px]">
              <PersonAutocomplete
                value={personId || null}
                onChange={(id) => {
                  const next = id ?? '';
                  setPersonId(next);
                  if (next) setUnassignedOnly(false);
                }}
                placeholder="Alle Personen"
              />
            </div>
            <select
              value={albumFilter}
              onChange={(e) => setAlbumFilter(e.target.value)}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">Alle Alben</option>
              <option value="none">(ohne Album)</option>
              {albums.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} ({a.mediaCount})
                </option>
              ))}
            </select>
            <label className="flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={unassignedOnly}
                onChange={(e) => {
                  setUnassignedOnly(e.target.checked);
                  if (e.target.checked) setPersonId('');
                }}
                className="rounded border-slate-300"
              />
              Nur nicht zugeordnete
            </label>
            {hasActiveFilter && (
              <button
                onClick={resetFilters}
                className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
              >
                Zurücksetzen
              </button>
            )}
          </div>

          {stats && stats.topTags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 border-t border-slate-100 pt-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                Tags:
              </span>
              {stats.topTags.slice(0, 12).map((t) => (
                <button
                  key={t.tag}
                  onClick={() => setActiveTag(activeTag === t.tag ? '' : t.tag)}
                  className={`rounded-full px-2 py-0.5 text-xs transition ${
                    activeTag === t.tag
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {t.tag}
                  <span className="ml-1 opacity-70">{t.count}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Inhalt */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white py-16 text-center">
            <Archive className="h-12 w-12 text-slate-300" aria-hidden="true" />
            <h2 className="mt-3 text-lg font-semibold text-slate-800">
              {hasActiveFilter ? 'Keine Treffer' : 'Das Archiv ist noch leer'}
            </h2>
            <p className="mt-1 max-w-sm text-sm text-slate-500">
              {hasActiveFilter
                ? 'Passe die Filter an oder setze sie zurück, um mehr Einträge zu sehen.'
                : 'Lade Fotos, Urkunden, Briefe oder andere Dokumente hoch, um deine Familienschätze zu bewahren.'}
            </p>
            {canManageArchive && !hasActiveFilter && (
              <button
                onClick={() => setShowUpload(true)}
                className="mt-4 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                Erste Datei hochladen
              </button>
            )}
          </div>
        ) : (
          <>
            <p className="text-xs text-slate-500">
              {items.length} von {total} Eintrag{total === 1 ? '' : 'e'} geladen
            </p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
              {items.map((item, i) => (
                <ArchiveCard
                  key={item.id}
                  item={item}
                  selected={selectedIds.has(item.id)}
                  selectionMode={selectedIds.size > 0}
                  onToggleSelect={() => toggleSelect(item.id)}
                  onClick={() => setSelectedIndex(i)}
                />
              ))}
            </div>

            {/* Infinite-Scroll-Sentinel */}
            <div ref={sentinelRef} className="flex h-16 items-center justify-center">
              {isFetchingNextPage && <LoadingSpinner />}
              {!hasNextPage && items.length > 0 && (
                <p className="text-xs text-slate-400">Alle Einträge geladen.</p>
              )}
            </div>
          </>
        )}
          </>
        )}
      </div>

      {showUpload && (
        <UploadModal
          onClose={() => setShowUpload(false)}
          albums={albums}
        />
      )}
      {selectedIndex !== null && items[selectedIndex] && (
        <DetailModal
          items={items}
          index={selectedIndex}
          albums={albums}
          onClose={() => setSelectedIndex(null)}
          onNavigate={(newIndex) => {
            setSelectedIndex(newIndex);
            // Wenn wir nahe am Ende sind und mehr laden können, Nachladen triggern
            if (newIndex >= items.length - 5 && hasNextPage && !isFetchingNextPage) {
              fetchNextPage();
            }
          }}
        />
      )}
      {editAlbum && (
        <EditAlbumModal album={editAlbum} onClose={() => setEditAlbum(null)} />
      )}
    </AppLayout>
  );
}

export default ArchivePage;
